import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label } from 'reactstrap';

const ColumnManager = ({ isOpen, toggle, availableColumns, visibleColumns, onColumnChange, defaultVisibleColumns }) => {
  const [localColumns, setLocalColumns] = useState(visibleColumns || defaultVisibleColumns);
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    if (visibleColumns && Array.isArray(visibleColumns)) {
      setLocalColumns(visibleColumns);
    } else {
      setLocalColumns([]);
    }
  }, [visibleColumns]);

  const handleToggle = (columnKey, checked) => {
    const column = availableColumns.find(col => col.key === columnKey);
    if (column?.fixed) return; // Don't allow toggling fixed columns

    if (checked) {
      // Add new column after fixed start columns (select, id) but before actions
      const selectIndex = localColumns.indexOf('select');
      const idIndex = localColumns.indexOf('id');
      const fixedStartCount = [selectIndex, idIndex].filter(i => i !== -1).length;
      const actionsIndex = localColumns.indexOf('actions');
      
      const newColumns = [...localColumns];
      if (actionsIndex !== -1) {
        // Insert before actions
        newColumns.splice(actionsIndex, 0, columnKey);
      } else {
        // No actions, just add at end
        newColumns.push(columnKey);
      }
      setLocalColumns(newColumns);
    } else {
      setLocalColumns(localColumns.filter(key => key !== columnKey));
    }
  };

  const safeLocalColumns = localColumns || [];
  const safeAvailableColumns = availableColumns || [];

  const handleDragStart = (index) => {
    const column = safeAvailableColumns.find(col => col.key === safeLocalColumns[index]);
    if (column?.fixed) return; // Don't allow dragging fixed columns
    
    // Don't allow dragging if it's in the fixed position area (select, id at start)
    const selectIndex = safeLocalColumns.indexOf('select');
    const idIndex = safeLocalColumns.indexOf('id');
    const fixedStartCount = [selectIndex, idIndex].filter(i => i !== -1).length;
    if (index < fixedStartCount) return;
    
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const column = safeAvailableColumns.find(col => col.key === safeLocalColumns[index]);
    if (column?.fixed) return; // Don't allow dropping on fixed columns

    // Don't allow dropping in fixed start positions (select, id)
    const selectIndex = safeLocalColumns.indexOf('select');
    const idIndex = safeLocalColumns.indexOf('id');
    const fixedStartCount = [selectIndex, idIndex].filter(i => i !== -1).length;
    if (index < fixedStartCount) return;

    // Don't allow dropping after the actions column (keep it at the end)
    const actionsIndex = safeLocalColumns.indexOf('actions');
    if (actionsIndex !== -1 && index >= actionsIndex) return;

    const newColumns = [...safeLocalColumns];
    const draggedItem = newColumns[draggedIndex];
    newColumns.splice(draggedIndex, 1);
    newColumns.splice(index, 0, draggedItem);

    setLocalColumns(newColumns);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleApply = () => {
    // Ensure select and id are at start, actions is at end
    const columnsWithoutFixed = localColumns.filter(key => key !== 'id' && key !== 'select' && key !== 'actions');
    const hasSelect = localColumns.includes('select');
    const hasId = localColumns.includes('id');
    const hasActions = localColumns.includes('actions');
    
    let finalColumns = [];
    if (hasSelect) finalColumns.push('select');
    if (hasId) finalColumns.push('id');
    finalColumns = [...finalColumns, ...columnsWithoutFixed];
    if (hasActions) finalColumns.push('actions');
    
    onColumnChange(finalColumns);
    toggle();
  };

  const handleReset = () => {
    const defaultColumns = safeAvailableColumns
      .filter(col => col.fixed || ['name', 'email', 'applicationDate','round1Score','round2Score', 'averageScore', 'degrees', 'program', 'themes', 'gender', 'state', 'country', 'status'].includes(col.key))
      .map(col => col.key);
    setLocalColumns(defaultColumns);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md" className="column-manager-modal">
      <ModalHeader toggle={toggle}>
        <span>⚙️</span> Manage Columns
      </ModalHeader>
      <ModalBody>
        <div className="mb-3">
          <p className="text-muted small">
            Select which columns to display and drag to reorder them. Selection and Application ID are always at the start, Actions column is always at the end. Fixed columns (Select, Application ID, Actions) cannot be hidden or moved.
          </p>
        </div>

        <div className="column-list ">
          {safeLocalColumns.map((columnKey, index) => {
            const column = safeAvailableColumns.find(col => col.key === columnKey);
            if (!column) return null;

            return (
              <div
                key={columnKey}
                draggable={!column.fixed}
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`d-flex align-items-center gap-3 px-4 py-3 border-bottom hover-bg-light ${column.fixed ? 'bg-light' : 'cursor-move'
                  } ${draggedIndex === index ? 'opacity-50' : ''}`}
              >
                <span className={`text-muted ${column.fixed ? 'invisible' : ''}`}>☰</span>
                <Input
                  type="checkbox"
                  checked={safeLocalColumns.includes(columnKey)}
                  onChange={(e) => handleToggle(columnKey, e.target.checked)}
                  disabled={column.fixed}
                />
                <span className="flex-grow-1 small">
                  {column.label}
                  {column.fixed && <span className="text-muted ms-2">(Fixed)</span>}
                </span>
              </div>
            );
          })}

          {/* Show hidden columns */}
          {safeAvailableColumns
            .filter(col => !safeLocalColumns.includes(col.key))
            .map(column => (
              <div
                key={column.key}
                className="d-flex align-items-center gap-3 px-4 py-3 border-bottom hover-bg-light opacity-60"
              >
                <span className="text-muted invisible">☰</span>
                <Input
                  type="checkbox"
                  checked={false}
                  onChange={(e) => handleToggle(column.key, e.target.checked)}
                />
                <span className="flex-grow-1 small text-muted">{column.label}</span>
              </div>
            ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleReset}>
          Reset to Default
        </Button>
        <Button color="primary" onClick={handleApply}>
          Apply Changes
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ColumnManager;
